import React from 'react';
import { Text, StyleSheet, View, StyleProp, ViewStyle, Platform, Pressable } from 'react-native';
import { Colors } from '../constants/theme';

interface Button3DProps {
  title: string;
  onPress: () => void;
  color?: string;
  style?: StyleProp<ViewStyle>;
  icon?: React.ReactNode;
  /**
   * ⚠️ ESTE PROP NO EXISTÍA, Y ESO ES PEOR QUE NO PODER DESHABILITAR.
   *
   * En React, un prop que el componente no declara NO DA ERROR: se recibe y se
   * tira. Así que cualquier pantalla que escribiera `<Button3D disabled={...}>`
   * tenía un botón que se veía normal, se dejaba tocar y hacía su trabajo — con
   * el código diciendo claramente que no debía. Es el mismo tropiezo que ya
   * costó una ronda con `hitSlop` en Pressable.
   *
   * Ahora el botón no dispara, no se hunde al tocarlo y se ve apagado: las tres
   * cosas, porque un botón que no responde PERO se ve encendido parece roto.
   */
  disabled?: boolean;
}

export default function Button3D({
  title,
  onPress,
  color = Colors.primary,
  style,
  icon,
  disabled = false,
}: Button3DProps) {
  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      accessibilityState={{ disabled }}
      style={[styles.wrapper, style, disabled && { opacity: 0.45 }]}
    >
      {({ pressed, hovered }: any) => {
        // En Web, 'hovered' viene como prop en el render function del Pressable
        // Apagado: ni se hunde ni se ilumina al pasar por encima.
        const isHovered = !disabled && (hovered || false);
        const translateY = !disabled && pressed ? 4 : isHovered ? -2 : 0;
        const shadowOpacity = !disabled && pressed ? 0 : isHovered ? 0.3 : 0.15;
        
        return (
          <View style={{ flex: 1, position: 'relative' }}>
            {/* Capa inferior (sombra/borde 3D) */}
            <View style={[styles.bottomLayer, { backgroundColor: darkenColor(color, 40) }]} />
            
            {/* Capa superior interactiva */}
            <View style={[
              styles.topLayer, 
              { 
                backgroundColor: isHovered ? lightenColor(color, 10) : color,
                transform: [{ translateY }],
                shadowOpacity
              }
            ]}>
              {icon && <View style={styles.iconContainer}>{icon}</View>}
              {/* DOS LINEAS COMO MAXIMO, Y QUE PUEDA ENCOGER.
                  Sin `flexShrink` el texto no cedia ni un pixel: con 18 px en
                  negrita «Soy Director(a) de Escuela» pide ~262 px, mas 34 del
                  icono y 40 de relleno = 336 px, y un telefono de 360 dp deja
                  unos 320 utiles. El texto se salia de la caja y Android lo
                  cortaba a media palabra (se ve en /role-selection). Dos lineas
                  de 20 px caben de sobra en los 54 px de alto. */}
              <Text style={styles.text} numberOfLines={2}>{title}</Text>
            </View>
          </View>
        );
      }}
    </Pressable>
  );
}

// Utilidades de color
function darkenColor(color: string, percent: number): string {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);
  R = Math.floor(R * (100 - percent) / 100);
  G = Math.floor(G * (100 - percent) / 100);
  B = Math.floor(B * (100 - percent) / 100);
  const getHex = (c: number) => {
    const hex = c.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${getHex(R)}${getHex(G)}${getHex(B)}`;
}

function lightenColor(color: string, percent: number): string {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);
  R = Math.min(255, Math.floor(R + (255 - R) * (percent / 100)));
  G = Math.min(255, Math.floor(G + (255 - G) * (percent / 100)));
  B = Math.min(255, Math.floor(B + (255 - B) * (percent / 100)));
  const getHex = (c: number) => {
    const hex = c.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${getHex(R)}${getHex(G)}${getHex(B)}`;
}

const styles = StyleSheet.create({
  wrapper: {
    marginVertical: 8,
    height: 60,
  },
  bottomLayer: {
    position: 'absolute',
    top: 6,
    left: 0,
    right: 0,
    height: 54,
    borderRadius: 16,
  },
  topLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 54,
    borderRadius: 16,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 8,
    elevation: 3,
  },
  iconContainer: {
    marginRight: 10,
  },
  text: {
    color: '#FFF',
    fontSize: 17,
    lineHeight: 20,
    fontWeight: 'bold',
    letterSpacing: 0.2,
    textAlign: 'center',
    flexShrink: 1,
  },
});
